import { useEffect } from "react";
import { Outlet, useParams } from "react-router-dom";
import { Alert } from "@mui/material";
import Snackbar from "@mui/material/Snackbar";
import { auth } from "@/firebase";
import { onAuthStateChanged } from "firebase/auth";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { closeSnackbar, setActiveOrganization } from "@/store/uiSlice";
import { getProfile, isLoggedInSelector, setUser } from "@/features/auth/slice";
import { useLazyFetchOrganizationAliasQuery } from "@/features/organizations/api/websiteApi";
import UpgradeModal from "@/components/UpgradeModal";

type Params = {
  organizationId?: string;
  organizationAlias?: string;
};

const IMPRESIONES_AEM = "njTiw4ZSrCL5dWje47UL";
const BITELINK = "bitelink";
const LA_CAMPANA = "WuTqUgHq1PV5j9hx5z0o";
const MARINA = "KTte3JfRIfsX7RnkortX";
const EL_CONTILITO = "WULidrEOFlYKhpjgYkw8";
const THE_STEAK = "fnPwZtBiX7JEjcox2AON";
const FAMILIA_CHANG = "xC6j7ywkJ78vfCnXYmfV";
const SOLUX_NICA = "LnBx0WRXwA9RrDZRgCTy";
const LIVIS_PIZZA = "dWLbe1NmSzLO9QUeKEdG";

const REDIRECTS: Record<string, string> = {
  ["/comercios/KfpVgEy9JIxRWe1hKnvx/menu/5dyrPo0uxiFRwbBDozaB"]: "/the-steak",
};

const ORGANIZATION_DOMAIN: Record<string, string> = {
  ["www.impresionesaem.com"]: IMPRESIONES_AEM,
  ["the-steaks.com"]: THE_STEAK,
  ["elcontilito.com"]: EL_CONTILITO,
  ["impresionesaem.com"]: IMPRESIONES_AEM,
  ["www.lacampananic.com"]: LA_CAMPANA,
  ["lacampananic.com"]: LA_CAMPANA,
  ["marinapuestadelsol.com"]: MARINA,
  ["familiachang.com"]: FAMILIA_CHANG,
  ["www.familiachang.com"]: FAMILIA_CHANG,
  ["soluxnica.com"]: SOLUX_NICA,
  ["bitelink.app"]: BITELINK,
  ["localhost:5173"]: BITELINK,
  ["livispizza.com"]: LIVIS_PIZZA,
};

const GlobalRoute = () => {
  const dispatch = useAppDispatch();
  const snackbar = useAppSelector((state) => state.ui.snackbar);
  const params = useParams<Params>();

  const [fetchAlias] = useLazyFetchOrganizationAliasQuery();

  const getOrganizationId = async () => {
    // check domain
    let id = null;
    const DOMAIN = document.location.host;
    const NORMALIZED_DOMAIN = DOMAIN.replace(/^www\./, "");

    // Verify redirects
    if (REDIRECTS[document.location.pathname]) {
      window.location.replace(REDIRECTS[document.location.pathname]);
    }

    const IS_ROOT = document.location.pathname === "/";
    const isBitelink = DOMAIN.includes("bitelink.app");

    //This is the correct way for avoiding a redirect
    const isDevMode = DOMAIN.includes("localhost");

    if (isBitelink || isDevMode) {
      if (IS_ROOT && !isDevMode) {
        window.location.replace("https://bitelink.website");
      }

      // check id in admin
      if (params?.organizationId) {
        id = params.organizationId;
      }

      // check alias
      if (params?.organizationAlias) {
        const alias = await fetchAlias(params.organizationAlias);

        if (alias.data?.organizationId) {
          id = alias.data.organizationId;
        }
      }
    } else {
      id = ORGANIZATION_DOMAIN[DOMAIN] || ORGANIZATION_DOMAIN[NORMALIZED_DOMAIN];
    }

    return id || (document.location.pathname.startsWith("/comercios") ? null : BITELINK);
  };

  const checkOrganization = async () => {
    const organizationId = await getOrganizationId();

    if (organizationId) {
      dispatch(setActiveOrganization(organizationId));
    }
  };

  useEffect(() => {
    checkOrganization();
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user: any) => {
      if (user) {
        dispatch(
          setUser({
            uid: user.uid,
            email: user.email,
            emailVerified: user.emailVerified,
            image: user.photoURL,
          }),
        );

        dispatch(getProfile(user.uid));
      }
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Outlet />
      <Snackbar
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        open={snackbar.isOpen}
        onClose={() => dispatch(closeSnackbar())}
        autoHideDuration={4000}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
      <UpgradeModal />
    </>
  );
};

export default GlobalRoute;
